import 'immer';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import type { TaxCode } from '@repo/api-contract';
import ApiClient from '../../util/api-client';
import type { RootState } from '../store';

export type SalesTaxSearchBy = 'codeName';

export type SalesTaxSearchInput = {
  query: string;
  searchBy: SalesTaxSearchBy;
  size: number;
  page: number;
};

export type SalesTaxSearchResponse = {
  total: number;
  size: number;
  page: number;
  results: TaxCode[];
};

export interface SalesTaxSearchState {
  searchInput: string;
  openSalesTaxSearchModal: boolean;
  resp?: SalesTaxSearchResponse;
  searchBy: SalesTaxSearchBy;
  loading: boolean;
  searchLoading: boolean;
  allTaxCodes: TaxCode[];
  selectedTaxCodes: TaxCode[];
  multiSelect: boolean;
  onImport?: (selectedTaxCodes: TaxCode[]) => void;
  onCancel?: () => void;
}

const initialState: SalesTaxSearchState = {
  searchInput: '',
  openSalesTaxSearchModal: false,
  resp: undefined,
  searchBy: 'codeName',
  loading: false,
  searchLoading: false,
  allTaxCodes: [],
  selectedTaxCodes: [],
  multiSelect: false,
  onImport: undefined,
  onCancel: undefined,
};

export const SalesTaxSearchModalSlice = createSlice({
  name: 'SalesTaxSearch',
  initialState,
  reducers: {
    openSearchSalesTaxModal: (
      state,
      action: PayloadAction<{
        onImport?: (selectedTaxCodes: TaxCode[]) => void;
        onCancel?: () => void;
        multiSelect?: boolean;
      }>,
    ) => ({
      ...initialState,
      openSalesTaxSearchModal: true,
      onImport: action.payload.onImport,
      onCancel: action.payload.onCancel,
      multiSelect: action.payload.multiSelect ?? false,
    }),
    closeModal: (state) => ({
      ...state,
      openSalesTaxSearchModal: false,
    }),
    setSearchInput: (state, action: PayloadAction<string>) => ({
      ...state,
      searchInput: action.payload,
    }),
    setSearchBy: (state, action: PayloadAction<SalesTaxSearchBy>) => ({
      ...state,
      searchBy: action.payload,
    }),
    setResults: (
      state,
      action: PayloadAction<SalesTaxSearchResponse | undefined>,
    ) => ({
      ...state,
      resp: action.payload,
    }),
    setSearchLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      searchLoading: action.payload,
    }),
    setSelectedTaxCodes: (state, action: PayloadAction<TaxCode[]>) => {
      state.selectedTaxCodes = action.payload;
    },
    setAllTaxCodes: (state, action: PayloadAction<TaxCode[]>) => ({
      ...state,
      allTaxCodes: action.payload,
    }),
  },
});

export const {
  openSearchSalesTaxModal,
  closeModal,
  setSearchInput,
  setSearchBy,
  setResults,
  setSearchLoading,
  setSelectedTaxCodes,
  setAllTaxCodes,
} = SalesTaxSearchModalSlice.actions;

export default SalesTaxSearchModalSlice.reducer;

function filterTaxCodes(taxCodes: TaxCode[], query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return taxCodes;

  return taxCodes.filter((taxCode) =>
    taxCode.codeName.toLowerCase().includes(normalizedQuery),
  );
}

export const searchSalesTaxCodes =
  (input: SalesTaxSearchInput) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      dispatch(setSearchLoading(true));
      let allTaxCodes = getState().salesTaxSearchModalSlice.allTaxCodes;

      if (allTaxCodes.length === 0) {
        const response = await ApiClient.allTaxCodes.query();
        allTaxCodes = response.results;
        dispatch(setAllTaxCodes(allTaxCodes));
      }

      const filtered = filterTaxCodes(allTaxCodes, input.query);
      const start = input.page * input.size;

      dispatch(
        setResults({
          total: filtered.length,
          size: input.size,
          page: input.page,
          results: filtered.slice(start, start + input.size),
        }),
      );
    } catch (error: unknown) {
      dispatch(setResults(undefined));
    } finally {
      dispatch(setSearchLoading(false));
    }
  };
